import {
  faCaretDown,
  faCaretUp,
  faCheck,
  faSearch,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverButton } from '@headlessui/react';
import classNames from 'classnames';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import {
  areFacetedFilterValuesEqual,
  FacetedFilterValueItem,
} from '../../helpers/arrayUtils'; // Adjusted path
import { useBreakpoint } from '../../hooks/useBreakpoint';
import { useScrollTrigger } from '../../hooks/useScrollTrigger';
import { useContacts } from '../../routes/resources/contacts';
import { Pill } from './nucleus/Pill';

export type FacetedFilterOption = {
  $group?: string;
  label?: string;
  key: string;
  value: string[];
  type?: string;
};

type Props = {
  label: string;
  options: FacetedFilterOption[];
  value: { key: string; value: string[] }[];
  inFilterBar?: boolean;
  onChange: (newValue: { key: string; value: string[] }[]) => void;
  onOpenChange?: (isOpen: boolean) => void;
  navScrollDetectionEnabled?: boolean;
  resolveContacts?: boolean;
  /** If true, this is specifically for attribute filtering and will handle contact resolution automatically */
  isAttributeFilter?: boolean;
};

function truncatedLabel(label: string) {
  if (label.length > 30) {
    return label.slice(0, 27) + '...';
  }
  return label;
}

function getSubLabel(label: string, values: string[]) {
  return truncatedLabel(
    values.length > 1
      ? `${label} • ${values.length}`
      : `${label} is ${values[0]}`
  );
}

export function FacetedFilterSelect({
  label,
  options,
  value,
  inFilterBar = true,
  onChange,
  onOpenChange,
  navScrollDetectionEnabled = true,
  resolveContacts = true,
  isAttributeFilter = false,
}: Props) {
  const { isMobile, isTablet } = useBreakpoint();
  // Note: isTablet targets < 700px (phones), isMobile targets < 1024px (phones + tablets)
  // Phones (< 700px): accordion-style single dialog
  // All other devices (>= 700px): hover-based dual dialog

  // Determine if contacts are needed for contact attribute resolution
  const shouldResolveContacts = resolveContacts || isAttributeFilter;

  // Query contacts for contact attribute resolution (only when needed)
  const contacts = useContacts({ pause: !shouldResolveContacts });

  // State for dialog stages and selections
  const [dialogStage, setDialogStage] = useState<'categories' | 'values'>(
    'categories'
  );
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [selected, setSelected] =
    useState<{ key: string; value: string[] }[]>(value);
  // State for expanded categories in mobile accordion view
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Refs for hover debounce and mouse tracking
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMouseInDialogsRef = useRef<boolean>(false);

  // Popper setup
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );
  // Removed arrow element handling (unused)
  const [categoryItemElement, setCategoryItemElement] =
    useState<HTMLDivElement | null>(null);
  const [valuesPopperElement, setValuesPopperElement] =
    useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 8] } },
      {
        name: 'flip',
        options: { fallbackPlacements: ['top-start', 'bottom-end', 'top-end'] },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 10,
          boundary: 'clippingParents',
          altBoundary: false,
          rootBoundary: 'viewport',
          tether: false,
        },
      },
    ],
  });

  const valuesPopper = usePopper(categoryItemElement, valuesPopperElement, {
    placement: 'right-start',
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [-8, 16] } },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['left-start', 'right-end', 'left-end'],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 10,
          boundary: 'clippingParents',
          altBoundary: false,
          rootBoundary: 'viewport',
          tether: false,
        },
      },
    ],
  });

  // Use the same scroll threshold as NavContainer for consistency
  const MOBILE_SCROLL_THRESHOLD = 150;

  const isNavHidden = useScrollTrigger({
    threshold: MOBILE_SCROLL_THRESHOLD,
    enabled: isMobile && navScrollDetectionEnabled,
  });

  // Sync selected state with external value changes
  useEffect(() => {
    if (
      !areFacetedFilterValuesEqual(
        selected as FacetedFilterValueItem[],
        value as FacetedFilterValueItem[]
      )
    ) {
      setSelected(value);
    }
  }, [value, selected]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Helper: Extract unique categories from options
  // Categories are based on option labels/keys
  const categories = useMemo(() => {
    const uniqueCats = new Set<string>();
    options.forEach((o) => {
      const categoryName = o.label || o.key;
      uniqueCats.add(categoryName);
    });
    return Array.from(uniqueCats);
  }, [options]);

  // Helper: Resolve contact IDs to names for display
  const resolveContactValue = useMemo(() => {
    return (value: string, categoryName?: string) => {
      if (!shouldResolveContacts) return value;

      const category = categoryName || activeCategory;
      if (!category) return value;

      // Check if this category is a contact attribute by looking at the options
      const categoryOption = options.find(
        (o) => (o.label || o.key) === category
      );
      if (categoryOption && categoryOption.type === 'contact') {
        const contact = contacts.find((c) => c.id === value);
        return contact ? contact.name : value; // fallback to ID if contact not found
      }

      return value;
    };
  }, [shouldResolveContacts, contacts, options, activeCategory]);

  // Helper: Get values for active category
  const categoryValues = useMemo(() => {
    if (!activeCategory) return [];
    const option = options.find((o) => (o.label || o.key) === activeCategory);
    return option?.value || [];
  }, [options, activeCategory]);

  // Helper: Get the key for the active category
  const activeCategoryKey = useMemo(() => {
    if (!activeCategory) return null;
    const option = options.find((o) => (o.label || o.key) === activeCategory);
    return option?.key || null;
  }, [options, activeCategory]);

  // Helper: Check if a value is selected for a category
  const isValueSelected = (val: string, categoryKey?: string) => {
    const key = categoryKey || activeCategoryKey;
    if (!key) return false;
    const currentSelection = selected.find((s) => s.key === key);
    return currentSelection?.value.includes(val) ?? false;
  };

  // Helper: Toggle category expansion in mobile accordion view
  const handleToggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  // Handler: Open values dialog when hovering a category
  const handleCategoryHover = (
    categoryName: string,
    element: HTMLDivElement
  ) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // If switching to a different category, update immediately for smoother transition
    if (activeCategory && activeCategory !== categoryName) {
      setCategoryItemElement(element);
      setActiveCategory(categoryName);
      setDialogStage('values');
    } else {
      // Set the category item as reference for values popper
      setCategoryItemElement(element);

      // Debounce hover to prevent flickering (reduced from 200ms for faster response)
      hoverTimeoutRef.current = setTimeout(() => {
        setActiveCategory(categoryName);
        setDialogStage('values');
      }, 150);
    }
  };

  // Handler: Mouse enters either dialog - keep both open
  const handleDialogMouseEnter = () => {
    isMouseInDialogsRef.current = true;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  // Handler: Mouse leaves either dialog - start close timer
  const handleDialogMouseLeave = () => {
    isMouseInDialogsRef.current = false;

    // Close values dialog after a short delay if mouse hasn't re-entered
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isMouseInDialogsRef.current) {
        // Return to categories view
        setDialogStage('categories');
        setActiveCategory(null);
        setCategoryItemElement(null);
      }
    }, 300);
  };

  // Handler: Toggle value selection (apply immediately)
  const handleValueToggle = (val: string, categoryKey?: string) => {
    const key = categoryKey || activeCategoryKey;
    if (!key) return;

    // Get current selections for this category
    const currentSelection = selected.find((s) => s.key === key);
    const currentValues = currentSelection?.value || [];

    // Toggle the value
    const nextValues = currentValues.includes(val)
      ? currentValues.filter((v) => v !== val)
      : [...currentValues, val];

    // Update selected state and notify parent
    const newSelected = [...selected.filter((s) => s.key !== key)];
    if (nextValues.length > 0) {
      newSelected.push({ key, value: nextValues });
    }

    setSelected(newSelected);
    onChange(newSelected);
  };

  // Removed unused per-category clear handler

  // Handler: Clear all selections across all categories
  const handleClearAll = () => {
    setSelected([]);
    onChange([]);
  };

  // Filter categories by search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categories;
    return categories.filter((cat) =>
      cat.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  // Track last open state to avoid spamming onOpenChange
  const lastOpenRef = useRef<boolean>(false);
  // Store closePopover function from render prop so we can use it in effects
  const closePopoverRef = useRef<(() => void) | null>(null);
  // Track popover open state directly
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  // Ref to track the render-prop open value to sync it via effect
  const renderPropOpenRef = useRef<boolean>(false);

  // Close the popover when clicking anywhere outside the FacetedFilterSelect UI
  // We explicitly treat clicks inside the trigger button, categories popper, or values popper
  // as "inside"; everything else (including surrounding SiteSelect content) is "outside".
  useEffect(() => {
    if (!popoverOpen) {
      return;
    }

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) {
        return;
      }

      const clickedInsideTrigger = referenceElement?.contains(target) ?? false;
      const clickedInsideCategoriesPopper =
        popperElement?.contains(target) ?? false;
      const clickedInsideValuesPopper =
        valuesPopperElement?.contains(target) ?? false;

      if (
        clickedInsideTrigger ||
        clickedInsideCategoriesPopper ||
        clickedInsideValuesPopper
      ) {
        // Click is within the FacetedFilterSelect UI; let Headless UI handle it.
        return;
      }

      // Click is genuinely outside this filter; close the popover.
      closePopoverRef.current?.();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [popoverOpen, referenceElement, popperElement, valuesPopperElement]);

  // Auto-close on mobile when nav is hidden
  useEffect(() => {
    if (!isMobile || !popoverOpen || !isNavHidden) {
      return;
    }

    closePopoverRef.current?.();
  }, [isMobile, popoverOpen, isNavHidden]);

  // Sync Headless UI open value to local state
  // This effect ensures popoverOpen stays in sync with the render-prop open value
  // We use renderPropOpenRef which is set inside the render prop to avoid state updates during render
  // useLayoutEffect runs synchronously after DOM mutations, ensuring we sync after every render
  useLayoutEffect(() => {
    if (renderPropOpenRef.current !== popoverOpen) {
      setPopoverOpen(renderPropOpenRef.current);
    }
  });

  // Handle popover state changes and reset internal state when closing
  useEffect(() => {
    if (popoverOpen !== lastOpenRef.current) {
      lastOpenRef.current = popoverOpen;
      onOpenChange?.(popoverOpen);

      if (!popoverOpen) {
        setDialogStage('categories');
        setActiveCategory(null);
        setSearchQuery('');
        setCategoryItemElement(null);
        setExpandedCategories(new Set());
        isMouseInDialogsRef.current = false;
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      }
    }
  }, [popoverOpen, onOpenChange]);

  // Check if there are valid options - must come after all hooks to follow Rules of Hooks
  const hasValidOptions = useMemo(() => {
    return (
      options.length > 0 && options.some((o) => o.value && o.value.length > 0)
    );
  }, [options]);

  // Don't render anything if there are no valid options
  if (!hasValidOptions) {
    return null;
  }

  return (
    <Popover>
      {({ open, close: closePopover }) => {
        // Capture latest close handler for effects
        closePopoverRef.current = closePopover;
        // Store render-prop open value in ref so useEffect can sync it to state
        renderPropOpenRef.current = open;

        // Build data for badge display
        const data = (open ? selected : value).map(({ key, value }) => ({
          id: key,
          label: options.find((o) => o.key === key)?.label ?? key,
          value,
        }));

        return (
          <>
            {/* Badge Pills */}
            <div className='flex w-full flex-row overflow-x-auto'>
              <PopoverButton
                ref={setReferenceElement}
                className='mx-[3px] my-2 rounded-full'
              >
                <Pill
                  type={inFilterBar ? 'filter' : 'button'}
                  active={data.length > 0}
                  hasParent
                >
                  {truncatedLabel(label)}
                  {data.length > 0 && (
                    <span
                      className='max-h-full cursor-pointer pt-0.5'
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange([]);
                        setSelected([]);
                      }}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </span>
                  )}
                </Pill>
              </PopoverButton>
              {data
                .filter((v) => v.value.length > 0)
                .map((v) => (
                  <Pill key={v.id} type='filter' active={true}>
                    {getSubLabel(v.label, v.value)}
                    <span
                      className='max-h-full cursor-pointer pt-0.5'
                      onClick={() => {
                        onChange(selected.filter((s) => s.key !== v.id));
                        setSelected(selected.filter((s) => s.key !== v.id));
                      }}
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </span>
                  </Pill>
                ))}
            </div>

            {/* First Dialog - Categories */}
            {open &&
              ReactDOM.createPortal(
                <div
                  ref={setPopperElement}
                  style={styles.popper}
                  {...attributes.popper}
                  className='prevent-drag-scroll border-grey-6 z-[10001] min-w-80 rounded-[3px] border bg-white shadow-md'
                  onMouseEnter={!isTablet ? handleDialogMouseEnter : undefined}
                  onMouseLeave={!isTablet ? handleDialogMouseLeave : undefined}
                >
                  <div className='flex flex-col gap-2 p-3'>
                    {/* Search Bar */}
                    <div className='flex h-[42px] items-center gap-1 rounded-full bg-grey-3 px-2'>
                      <div className='flex h-12 w-12 items-center justify-center'>
                        <FontAwesomeIcon
                          icon={faSearch}
                          className='text-copy-light'
                        />
                      </div>
                      <input
                        type='text'
                        placeholder='Search attributes'
                        aria-label='Search attributes'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className='text-copy flex-1 bg-transparent text-base outline-none placeholder:text-copy-light'
                      />
                    </div>

                    {/* Categories List */}
                    <div className='max-h-[362px] overflow-y-auto'>
                      {filteredCategories.map((category) => {
                        const option = options.find(
                          (o) => (o.label || o.key) === category
                        );
                        const categoryKey = option?.key;
                        const categoryValues = option?.value || [];
                        const isHighlighted = selected.some(
                          (s) => s.key === categoryKey
                        );
                        const isActiveCategory =
                          dialogStage === 'values' &&
                          activeCategory === category;
                        const isExpanded = isTablet
                          ? expandedCategories.has(category)
                          : isActiveCategory;

                        return (
                          <div key={category}>
                            {/* Category Header */}
                            <div
                              role='button'
                              tabIndex={0}
                              aria-label={`Filter by ${category}`}
                              aria-expanded={isTablet ? isExpanded : undefined}
                              className={classNames(
                                'flex cursor-pointer items-center justify-between overflow-clip px-2.5 py-1.5 transition-colors duration-200',
                                isHighlighted || isActiveCategory
                                  ? 'bg-surface-container-high hover:bg-surface-container-bright'
                                  : 'bg-white hover:bg-brand-ghost hover:bg-opacity-10'
                              )}
                              onMouseEnter={
                                !isTablet
                                  ? (e) =>
                                      handleCategoryHover(
                                        category,
                                        e.currentTarget
                                      )
                                  : undefined
                              }
                              onClick={
                                isTablet
                                  ? () => handleToggleCategory(category)
                                  : (e) =>
                                      handleCategoryHover(
                                        category,
                                        e.currentTarget
                                      )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  if (isTablet) {
                                    handleToggleCategory(category);
                                  } else {
                                    handleCategoryHover(
                                      category,
                                      e.currentTarget
                                    );
                                  }
                                }
                              }}
                            >
                              <div className='flex items-center px-2.5 py-1'>
                                <p className='text-copy text-base'>
                                  {category}
                                </p>
                              </div>
                              {isTablet && (
                                <div className='flex h-5 w-5 items-center justify-center'>
                                  <FontAwesomeIcon
                                    icon={isExpanded ? faCaretUp : faCaretDown}
                                    className='text-copy-light'
                                  />
                                </div>
                              )}
                            </div>

                            {/* Values List - shown inline on mobile when expanded */}
                            {isTablet &&
                              isExpanded &&
                              categoryValues.length > 0 && (
                                <div className='border-grey-6 bg-grey-1 border-t'>
                                  {categoryValues.map((val) => {
                                    const valSelected = isValueSelected(
                                      val,
                                      categoryKey
                                    );

                                    return (
                                      <div
                                        key={val}
                                        role='checkbox'
                                        aria-checked={valSelected}
                                        tabIndex={0}
                                        className={classNames(
                                          'flex cursor-pointer items-center justify-between overflow-clip px-4 py-2 transition-colors duration-200',
                                          valSelected
                                            ? 'bg-surface-container-high hover:bg-surface-container-bright'
                                            : 'bg-grey-1 hover:bg-brand-ghost hover:bg-opacity-10'
                                        )}
                                        onClick={() =>
                                          handleValueToggle(val, categoryKey)
                                        }
                                        onKeyDown={(e) => {
                                          if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                          ) {
                                            e.preventDefault();
                                            handleValueToggle(val, categoryKey);
                                          }
                                        }}
                                      >
                                        <div className='flex items-center px-2.5 py-1'>
                                          <p className='text-copy text-base'>
                                            {resolveContactValue(val, category)}
                                          </p>
                                        </div>
                                        {valSelected && (
                                          <div className='flex h-5 w-5 items-center justify-center'>
                                            <FontAwesomeIcon
                                              icon={faCheck}
                                              className='text-brand'
                                            />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Actions Footer - always visible */}
                  <div className='border-grey-6 flex items-center justify-between border-t px-4 py-2.5'>
                    <button
                      className='text-sm text-copy-light'
                      aria-label='Clear all selected filters'
                      onClick={handleClearAll}
                    >
                      Clear
                    </button>
                    <button
                      className='text-sm text-copy-light'
                      aria-label='Confirm and close filter menu'
                      onClick={() => closePopover()}
                    >
                      OK
                    </button>
                  </div>
                </div>,
                document.body
              )}

            {/* Second Dialog - Values (desktop/tablet only) */}
            {open &&
              !isTablet &&
              dialogStage === 'values' &&
              activeCategory &&
              ReactDOM.createPortal(
                <div
                  ref={setValuesPopperElement}
                  style={{
                    ...valuesPopper.styles.popper,
                    // Add smooth transition for position changes (popper uses transform)
                    // Using will-change to hint browser for optimization
                    willChange: 'transform',
                  }}
                  {...valuesPopper.attributes.popper}
                  className='prevent-drag-scroll border-grey-6 z-[10002] min-w-80 rounded-md border bg-white shadow-md transition-transform duration-150 ease-out'
                  onMouseEnter={handleDialogMouseEnter}
                  onMouseLeave={handleDialogMouseLeave}
                >
                  <div className='flex flex-col'>
                    {/* Values List */}
                    <div className='max-h-[300px] overflow-y-auto p-3'>
                      {categoryValues.map((val) => {
                        const selected = isValueSelected(val);

                        return (
                          <div
                            key={val}
                            role='checkbox'
                            aria-checked={selected}
                            tabIndex={0}
                            className={classNames(
                              'flex cursor-pointer items-center justify-between overflow-clip px-1.5 py-1.5 transition-colors duration-200',
                              selected
                                ? 'bg-surface-container-high hover:bg-surface-container-bright'
                                : 'bg-white hover:bg-brand-ghost hover:bg-opacity-10'
                            )}
                            onClick={() => handleValueToggle(val)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleValueToggle(val);
                              }
                            }}
                          >
                            <div className='flex items-center px-2.5 py-1'>
                              <p className='text-copy text-base'>
                                {resolveContactValue(val, activeCategory)}
                              </p>
                            </div>
                            {selected && (
                              <div className='flex h-5 w-5 items-center justify-center'>
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className='text-brand'
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>,
                document.body
              )}
          </>
        );
      }}
    </Popover>
  );
}
